import { AlertTriangle, ArrowLeft, Home, Plus, RefreshCw } from 'lucide-react';
import type React from 'react';
import { cn } from '../utils';
import { Button } from './ui/button';
import { Skeleton } from './ui/skeleton';

interface ListLoadingSkeletonProps {
    title?: string;
    message: string;
    PageIcon?: React.ComponentType<{ className?: string }>;
    pageIconColor?: string;
}

export interface DataStateWrapperProps {
    // State flags
    isLoading?: boolean;
    isError?: boolean;
    error?: Error | null;
    isEmpty?: boolean;

    // Content
    children: React.ReactNode;

    // Variant determines layout and skeleton type
    variant?: 'list' | 'detail' | 'section' | 'inline';

    // Loading state customization
    loadingMessage?: string;
    loadingSkeleton?: React.ComponentType;

    // Error state customization
    errorMessage?: string;
    errorDescription?: string;
    errorIcon?: React.ComponentType<{ className?: string }>;

    // Empty state customization
    emptyMessage?: string;
    /** Default subtitle when omitted; pass `''` to hide. */
    emptyDescription?: string;
    emptyIcon?: React.ComponentType<{ className?: string }>;
    emptyCompact?: boolean;

    // Actions
    onRetry?: () => void;
    onBack?: () => void;
    onCreateNew?: () => void;
    customActions?: React.ReactNode;

    // List/Management variant specific props
    title?: string;
    pageIcon?: React.ComponentType<{ className?: string }>;
    pageIconColor?: string;
    createButtonText?: string;

    // Layout customization
    className?: string;
    containerHeight?: string; // For section variant
}

// List page loading skeleton — mimics real page structure (title + filter bar + table)
const ListLoadingSkeleton = ({ title, message, PageIcon, pageIconColor }: ListLoadingSkeletonProps) => (
    <div className="space-y-6 p-6">
        {/* Title header area */}
        <div className="flex w-full flex-wrap items-center justify-between gap-4 py-4">
            <div className="flex items-center gap-3">
                {PageIcon ? <PageIcon className={`h-8 w-8 shrink-0 ${pageIconColor ?? 'text-primary'}`} /> : <div className="h-8 w-8 rounded bg-gray-100" />}
                <div className="space-y-1.5">
                    {title ? <h1 className="font-medium text-3xl text-primary">{title}</h1> : <div className="h-8 w-48 rounded bg-gray-100" />}
                    <div className="h-4 w-20 animate-pulse rounded bg-gray-200/70" />
                </div>
            </div>
            <div className="flex gap-2">
                <div className="h-9 w-28 rounded-md border border-gray-200 bg-gray-50" />
                <div className="h-9 w-28 rounded-md bg-gray-100" />
            </div>
        </div>

        {/* Filter bar card skeleton */}
        <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
            <div className="flex items-center gap-2">
                <div className="h-8 w-20 rounded-full border border-gray-200 bg-gray-50" />
                <div className="h-8 w-24 rounded-full border border-gray-200 bg-gray-50" />
                <div className="h-8 w-20 rounded-full border border-gray-200 bg-gray-50" />
                <div className="h-8 w-28 rounded-full border border-gray-200 bg-gray-50" />
                <div className="ml-auto flex gap-1.5">
                    <div className="h-8 w-20 rounded-md border border-gray-200 bg-gray-50" />
                    <div className="h-8 w-8 rounded-md border border-gray-200 bg-gray-50" />
                </div>
            </div>
        </div>

        {/* Table skeleton */}
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            {/* Table header row */}
            <div className="border-b border-gray-200 bg-gray-50/50 px-4 py-3">
                <div className="grid grid-cols-8 gap-4">
                    {Array.from({ length: 8 }).map((_, i) => (
                        <div key={i} className="h-3 animate-pulse rounded bg-gray-300/50" />
                    ))}
                </div>
            </div>
            {/* Table data rows */}
            {Array.from({ length: 10 }).map((_, rowIdx) => (
                <div key={rowIdx} className="border-b border-gray-100 px-4 py-3.5 last:border-b-0">
                    <div className="grid grid-cols-8 gap-4">
                        {Array.from({ length: 8 }).map((_, colIdx) => (
                            <div
                                key={colIdx}
                                className="h-3 animate-pulse rounded bg-gray-200/60"
                                style={{ animationDelay: `${rowIdx * 75 + colIdx * 50}ms` }}
                            />
                        ))}
                    </div>
                </div>
            ))}
            {/* Pagination area */}
            <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50/30 px-4 py-2.5">
                <div className="h-3.5 w-44 rounded bg-gray-200/50" />
                <div className="flex items-center gap-1.5">
                    <div className="h-7 w-7 rounded border border-gray-200 bg-white" />
                    <div className="h-3.5 w-20 rounded bg-gray-200/50" />
                    <div className="h-7 w-7 rounded border border-gray-200 bg-white" />
                </div>
            </div>
        </div>

        <p className="text-center text-muted-foreground text-xs">{message}</p>
    </div>
);

// Detail page loading skeleton (complex layout mimic)
const DetailLoadingSkeleton = ({ message }: { message: string }) => (
    <div className="space-y-6 p-6">
        {/* Header skeleton */}
        <div className="flex items-center justify-between">
            <Skeleton className="h-8 w-32" />
            <div className="flex space-x-2">
                <Skeleton className="h-8 w-24" />
                <Skeleton className="h-8 w-24" />
            </div>
        </div>

        {/* Title and summary cards skeleton */}
        <div className="space-y-4">
            <Skeleton className="h-8 w-64" />
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="p-4 border rounded-lg">
                        <Skeleton className="h-4 w-16 mb-2" />
                        <Skeleton className="h-6 w-20" />
                    </div>
                ))}
            </div>
        </div>

        {/* Tabs skeleton */}
        <div className="border-b">
            <div className="flex space-x-6">
                {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-8 w-20" />
                ))}
            </div>
        </div>

        {/* Content area skeleton */}
        <div className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
        </div>

        <div className="flex items-center justify-center py-6">
            <p className="text-muted-foreground text-sm">{message}</p>
        </div>
    </div>
);

// Section loading skeleton (medium complexity)
const SectionLoadingSkeleton = ({ message }: { message: string }) => (
    <div className="flex flex-col items-center justify-center p-8 space-y-4">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <p className="text-muted-foreground text-sm">{message}</p>
    </div>
);

// Inline loading skeleton (minimal)
const InlineLoadingSkeleton = ({ message }: { message: string }) => (
    <div className="flex items-center space-x-2 py-2">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <span className="text-muted-foreground text-sm">{message}</span>
    </div>
);

export function DataStateWrapper({
    isLoading = false,
    isError = false,
    error,
    isEmpty = false,
    children,
    variant = 'section',
    loadingMessage = 'Loading...',
    loadingSkeleton: CustomLoadingSkeleton,
    errorMessage = 'Something went wrong',
    errorDescription,
    errorIcon: ErrorIcon = AlertTriangle,
    emptyMessage = 'No data available',
    emptyDescription,
    emptyIcon: EmptyIcon = Home,
    emptyCompact = false,
    onRetry,
    onBack,
    onCreateNew,
    customActions,
    title,
    pageIcon: PageIcon,
    pageIconColor = 'text-blue-600',
    createButtonText = 'Create New',
    className = '',
    containerHeight = 'h-96',
}: DataStateWrapperProps) {
    // Handle loading state
    if (isLoading) {
        if (CustomLoadingSkeleton) {
            return <CustomLoadingSkeleton />;
        }

        switch (variant) {
            case 'list':
                return <ListLoadingSkeleton title={title} message={loadingMessage} PageIcon={PageIcon} pageIconColor={pageIconColor} />;
            case 'detail':
                return <DetailLoadingSkeleton message={loadingMessage} />;
            case 'section':
                return (
                    <div className={`flex items-center justify-center ${containerHeight} ${className}`}>
                        <SectionLoadingSkeleton message={loadingMessage} />
                    </div>
                );
            case 'inline':
                return <InlineLoadingSkeleton message={loadingMessage} />;
        }
    }

    // Handle error state
    if (isError) {
        const errorContent = (
            <div className="text-center space-y-4">
                <ErrorIcon className="mx-auto h-12 w-12 text-red-500" />
                <div className="space-y-2">
                    <h3 className="font-semibold text-lg text-red-900">{errorMessage}</h3>
                    <p className="text-red-600 text-sm">{errorDescription || error?.message || 'An unexpected error occurred while loading data.'}</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 justify-center">
                    {onRetry && (
                        <Button onClick={onRetry} variant="outline" size="sm">
                            <RefreshCw className="mr-2 h-4 w-4" />
                            Try Again
                        </Button>
                    )}
                    {onBack && (
                        <Button onClick={onBack} variant="ghost" size="sm">
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            Go Back
                        </Button>
                    )}
                    {customActions}
                </div>
            </div>
        );

        switch (variant) {
            case 'list':
                return (
                    <div className={`space-y-6 p-6 ${className}`}>
                        {title && PageIcon && (
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    <PageIcon className={`h-8 w-8 ${pageIconColor}`} />
                                    <div>
                                        <h1 className="font-medium text-3xl">{title}</h1>
                                    </div>
                                </div>
                                {createButtonText && onCreateNew && (
                                    <Button onClick={onCreateNew}>
                                        <Plus className="mr-2 h-4 w-4" />
                                        {createButtonText}
                                    </Button>
                                )}
                            </div>
                        )}
                        <div className="flex items-center justify-center py-12">{errorContent}</div>
                    </div>
                );
            default:
                return <div className={`flex items-center justify-center ${containerHeight} ${className}`}>{errorContent}</div>;
        }
    }

    // Handle empty state
    if (isEmpty) {
        const resolvedEmptyDescription = emptyDescription === undefined ? 'There are no items to display at this time.' : emptyDescription;
        const emptyContent = (
            <div className={cn('text-center', emptyCompact ? 'space-y-2' : 'space-y-4')}>
                <EmptyIcon className={cn('mx-auto text-muted-foreground', emptyCompact ? 'h-8 w-8' : 'h-12 w-12')} />
                <div className={emptyCompact ? 'space-y-1' : 'space-y-2'}>
                    <h3 className={cn('font-semibold', emptyCompact ? 'text-sm' : 'text-lg')}>{emptyMessage}</h3>
                    {resolvedEmptyDescription ? <p className="text-muted-foreground text-sm">{resolvedEmptyDescription}</p> : null}
                </div>
                <div className="flex flex-col sm:flex-row gap-2 justify-center">
                    {onCreateNew && (
                        <Button onClick={onCreateNew} size="sm">
                            <Plus className="mr-2 h-4 w-4" />
                            {createButtonText}
                        </Button>
                    )}
                    {customActions}
                </div>
            </div>
        );

        switch (variant) {
            case 'list':
                return (
                    <div className={`space-y-6 p-6 ${className}`}>
                        {title && PageIcon && (
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    <PageIcon className={`h-5 w-5 ${pageIconColor}`} />
                                    <div>
                                        <h3 className="font-medium text-xl">{title}</h3>
                                    </div>
                                </div>
                                {createButtonText && onCreateNew && (
                                    <Button size="sm" onClick={onCreateNew}>
                                        <Plus className="mr-2 h-4 w-4" />
                                        {createButtonText}
                                    </Button>
                                )}
                            </div>
                        )}
                        <div className="flex items-center justify-center py-12">{emptyContent}</div>
                    </div>
                );
            default:
                return <div className={`flex items-center justify-center ${containerHeight} ${className}`}>{emptyContent}</div>;
        }
    }

    // Success state - render children
    return <div className={className}>{children}</div>;
}
